import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Input,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { customerTimezone, defaultTimezone } from "../../constants";
import { createTimezoneTitle } from "../../notifications/pages/GeneralNotifications/FrequencyNotificationsSection/TimezoneSelector";

const OnboardCustomerModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();

  const [customerDetails, setCustomerDetails] = useState({
    customerId: "",
    customerEmail: "",
    displayName: "",
    timezone: defaultTimezone,
    showNightlyFlow: false,
  });

  const [onSave, setOnSave] = useState(false);
  const [isAllPropertiesFilled, setIsAllPropertiesFilled] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const setAllProperties = () => {
    setCustomerDetails({
      customerId: "",
      customerEmail: "",
      displayName: "",
      timezone: defaultTimezone,
      showNightlyFlow: false,
    });
    setIsAllPropertiesFilled(false);
  };

  const { onboardNewCustomer } = useAPI();

  const handleSave = () => {
    setOnSave(true);
    onboardNewCustomer({
      customer_id: customerDetails.customerId,
      customer_email: customerDetails.customerEmail,
      display_name: customerDetails.displayName,
      timezone: customerDetails.timezone,
      show_nightly_flow: customerDetails.showNightlyFlow,
    })
      .then((response) => {
        trackEvent(`Onboarded new customer ${customerDetails.customerId}`);
        setShowSuccessModal(true);
      })
      .catch((error) => {
        trackEvent(`Failed to onboard customer ${customerDetails.customerId}`);
        if (error.response) {
          Toast.error(error.response.data);
        } else {
          Toast.error("Failed to onboard customer");
        }
      })
      .finally(() => {
        setOnSave(false);
      });
  };

  const handleCancel = () => {
    trackEvent("User cancelled the onboard customer dialog");
    setAllProperties();
    handleClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    handleCancel();
  };

  useEffect(() => {
    if (
      customerDetails.customerId &&
      customerDetails.customerEmail &&
      customerDetails.displayName &&
      customerDetails.timezone
    ) {
      setIsAllPropertiesFilled(true);
    } else {
      setIsAllPropertiesFilled(false);
    }
  }, [customerDetails]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
      >
        <Box
          sx={{
            width: 500,
            bgcolor: "background.paper",
            margin: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            overflowY: "auto",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: colors.common_white,
                pt: "5%",
              }}
              id="modal-modal-title"
            >
              <Typography variant="subtitle1" gutterBottom>
                Onboard New Customer
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ marginTop: "4px" }}
              >
                Enter customer details
              </Typography>
            </Box>
            <Box sx={{ padding: "32px" }}>
              {/* Customer ID */}
              <FormControl variant="standard" fullWidth>
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Customer ID
                </Typography>
                <Input
                  placeholder="Enter customer ID (e.g., customer.com)"
                  value={customerDetails.customerId}
                  onChange={(event) =>
                    setCustomerDetails((prevDetails) => ({
                      ...prevDetails,
                      customerId: event.target.value,
                    }))
                  }
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${colors.grey_32percent}`,
                      fontSize: "12px",
                      color: colors.common_black,
                    },
                  }}
                />
              </FormControl>
              <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

              {/* Admin Email */}
              <FormControl variant="standard" fullWidth>
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Admin Email
                </Typography>
                <Input
                  placeholder="Enter admin email"
                  value={customerDetails.customerEmail}
                  onChange={(event) =>
                    setCustomerDetails((prevDetails) => ({
                      ...prevDetails,
                      customerEmail: event.target.value,
                    }))
                  }
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${colors.grey_32percent}`,
                      fontSize: "12px",
                    },
                  }}
                />
              </FormControl>
              <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

              {/* Display Name */}
              <FormControl variant="standard" fullWidth>
                <Typography variant="subtitle2" fontSize={"14px"}>
                  Display Name
                </Typography>
                <Input
                  placeholder="Enter display name"
                  value={customerDetails.displayName}
                  onChange={(event) =>
                    setCustomerDetails((prevDetails) => ({
                      ...prevDetails,
                      displayName: event.target.value,
                    }))
                  }
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${colors.grey_32percent}`,
                      fontSize: "12px",
                    },
                  }}
                />
              </FormControl>
              <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

              {/* Timezone */}
              <FormControl variant="standard" fullWidth>
                <Typography
                  variant="subtitle2"
                  fontSize={"14px"}
                  sx={{ marginBottom: "8px" }}
                >
                  Timezone
                </Typography>
                <Select
                  value={customerDetails.timezone}
                  onChange={(e) =>
                    setCustomerDetails((prev) => ({
                      ...prev,
                      timezone: e.target.value,
                    }))
                  }
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  {Object.keys(customerTimezone).map((ianaTimezone) => (
                    <MenuItem key={ianaTimezone} value={ianaTimezone}>
                      {createTimezoneTitle(ianaTimezone)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Show Nightly Flow Feature Flag */}
              <FormControl variant="standard" fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customerDetails.showNightlyFlow}
                      onChange={(event) =>
                        setCustomerDetails((prevDetails) => ({
                          ...prevDetails,
                          showNightlyFlow: event.target.checked,
                        }))
                      }
                      sx={{
                        color: colors.primary_main,
                        "&.Mui-checked": {
                          color: colors.primary_main,
                        },
                      }}
                    />
                  }
                  label={
                    <>
                      <Typography variant="subtitle2" fontSize={"14px"}>
                        Enable "Show Nightly Flow" feature flag
                      </Typography>

                      <Typography variant="body2" fontSize={"12px"}>
                        This will enable the "Run Initial Scan" banner for the
                        customer after tool integration.
                      </Typography>
                    </>
                  }
                  sx={{ marginBottom: "16px" }}
                />
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <SecondaryButton
                onClick={handleCancel}
                sx={{
                  color: colors.primary_main,
                  borderRadius: "10px",
                  border: "none",
                  height: "30px",
                  fontSize: "12px",
                }}
              >
                Cancel
              </SecondaryButton>
              {onSave ? (
                <LoadingIcon />
              ) : (
                <PrimaryButton
                  onClick={handleSave}
                  disabled={!isAllPropertiesFilled || onSave}
                  style={{
                    color: colors.common_white,
                    backgroundColor:
                      isAllPropertiesFilled && !onSave
                        ? colors.primary_main
                        : colors.primary_light,
                    borderRadius: "10px",
                    border: "none",
                    cursor:
                      isAllPropertiesFilled && !onSave
                        ? "pointer"
                        : "not-allowed",
                  }}
                  size="small"
                >
                  Onboard
                </PrimaryButton>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        aria-labelledby="success-modal-title"
      >
        <Box
          sx={{
            width: 400,
            bgcolor: "background.paper",
            margin: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            borderRadius: "10px",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" id="success-modal-title" gutterBottom>
            Customer Onboarded Successfully
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            Remember to add the customer ID to the journal secret if you wish
            the journal to collect data.
          </Typography>
          <PrimaryButton
            onClick={handleSuccessModalClose}
            style={{
              color: colors.common_white,
              backgroundColor: colors.primary_main,
              borderRadius: "10px",
              border: "none",
            }}
          >
            OK
          </PrimaryButton>
        </Box>
      </Modal>
    </>
  );
};

export default OnboardCustomerModal;
